/**
 * Overlays — the one family CONTRACT.md has no section for.
 *
 * The mockups are screenshots and not one of them has a dialog open, so there is
 * no §2.16 to port. What there is instead is `ARCHITECTURE.md` §Frontend —
 * "shadcn/ui primitives (Dialog, Popover, Select…) restyled to those tokens" and
 * "Radix primitives via shadcn" under §Accessibility — and `portal.css`'s
 * §Overlays block, written in the contract's vocabulary out of the contract's
 * own tokens. `.docs/reports/09-report.md` proposes it as the section the
 * contract should gain.
 *
 * **This is the only file in the repository that imports `@radix-ui/*`.** What
 * Radix is here for is the four things a hand-rolled overlay gets wrong: the
 * focus trap, restoring focus to whatever opened it, `Escape`, and marking the
 * rest of the page `aria-hidden` while it is open.
 *
 * `ConfirmDialog` is the money one. §Edge cases: "requires typing nothing — a
 * single 'Approve $1,800.00 for WO-2026-014' button — but is not dismissed by
 * clicking outside". Both halves are here: the button says the amount and the
 * ref because a confirmation that says "Are you sure?" is a confirmation nobody
 * reads, and an outside click is refused because losing a money dialog to a
 * stray click is how the wrong thing gets approved on the second attempt.
 *
 * ## Three things Radix does not do for us, and they are all focus
 *
 * Radix traps focus and it handles `Escape`; the rest of the list at the top of
 * this comment turned out to be conditional on a `<Dialog.Trigger>` that most of
 * this app does not render. All three are fixed **here**, once, rather than at
 * twenty call sites — which is also the only place they *can* be fixed, because
 * this is the one file that may import `@radix-ui/*`.
 *
 *   1. **Focus goes back to the opener, and the opener is not always a trigger.**
 *      `DialogContentModal`'s own `onCloseAutoFocus` is
 *      `event.preventDefault(); context.triggerRef.current?.focus()` — and
 *      `triggerRef` is null for every dialog opened by a page holding `open` in
 *      state, which is most of them. Null focuses nothing, so focus fell to
 *      `<body>` and the next Tab restarted at the top of the page. So the element
 *      that had focus when `open` went true is captured — while that frame renders,
 *      which is before Radix's own autofocus and before anything the opening
 *      removes has gone — and restored on the way out.
 *
 *      **And sometimes there is no opener left to go back to** (session 24, S-3).
 *      Confirming *Cancel the invitation* removes the row the trigger was in, so
 *      `back.isConnected` is false and the same `<body>` happens for the same
 *      reason — on the dialogs where losing your place costs the most, because the
 *      thing the reader was on is gone and there is nothing on the screen left to
 *      look for it by. `focusAfterClose` below is the answer, and it is here rather
 *      than at the call site because every dialog whose action can delete its own
 *      trigger would otherwise need the same twelve lines; the two that exist today
 *      (`pages/client/settings/PeoplePanel.tsx` and
 *      `pages/studio/clientDetail/PeopleSection.tsx`) are not the last of them.
 *   2. **A dialog never opens on the button that commits money.** Radix's default
 *      is FocusScope's `focusFirst(tabbable candidates)`, and in an input-less
 *      confirmation the first tabbable is the ochre button — so *Approve $150.00 ·
 *      REQ-118* was one stray Enter from being approved. It opens on its first
 *      **field** instead, and on the panel itself when it has none. Both are
 *      WAI-ARIA's own two answers; which one you get is decided by whether there
 *      is anything to type, which is the honest reading of "where does this dialog
 *      want me".
 *   3. **Every dialog has a visible way out.** Escape is not a discoverable
 *      affordance and three dialogs (a member's role, a new work order, the dry-run
 *      client picker) offered nothing else, so a mouse could open them and not
 *      close them. `DialogClose` is in the head of every dialog, so no call site
 *      has to remember. It is a word rather than a glyph — CONTRACT.md §5 refuses
 *      an icon family, and "everything else that wanted a glyph got a word".
 */

import * as RadixDialog from '@radix-ui/react-dialog'
import * as RadixPopover from '@radix-ui/react-popover'
import * as RadixSelect from '@radix-ui/react-select'
import { useCallback, useRef, useState, type ReactNode } from 'react'

import { useEditing } from '../../shell/editing.js'
import { Button, ButtonRow } from './Button.js'
import { cx, type Common } from './common.js'

/**
 * What a dialog opens focused on when it has one — the first thing to fill in.
 *
 * Buttons are deliberately not in this list, and neither is `input[type=submit]`:
 * point 2 above is that a control which *does* something is never what a dialog
 * opens on. A field is not that; it is where the reader was going anyway.
 *
 * Nor is an input carrying `hidden`: `Drop`'s file input is one, it cannot take focus,
 * and a window whose first field it was — the request composer's receipt — opened
 * with the focus on nothing at all.
 */
const FIRST_FIELD = [
  'input:not([type="hidden"]):not([hidden]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
].join(', ')

/** The word every dialog's own dismiss control carries. */
export const DIALOG_CLOSE_LABEL = 'Close'

/**
 * What opened each overlay that is on the page, by the overlay's own panel.
 *
 * A window and a menu are each drawn in a portal — a sibling of the application, not
 * a descendant of whatever raised it — so the DOM has no path from a panel back to
 * the button that opened it. This is that path, kept by the two primitives that
 * raise one (`Dialog`, `Popover`), and read by `lineageOf` below. Weak, so a panel
 * that has gone takes its entry with it.
 */
const OPENED_BY = new WeakMap<HTMLElement, HTMLElement>()

/**
 * The chain from an element up to `<body>`, exclusive — captured while the opener is
 * still on the page, because once it has been removed its parents are unreachable
 * from it.
 *
 * **And on, through whatever opened the overlay it sits in.** An entry in a menu, or
 * a button in a window that hands over to another window, is inside a portal that is
 * going away *as the new window opens*: every ancestor it has goes with it, so the
 * chain above ends in nothing that survives and focus fell to `<body>` — after every
 * Comment, Log time, Block, Give it back and Cancel raised from a More menu. What
 * opened that menu is still on the page, and is where the reader was; so the chain
 * carries on from there, and from what opened *that*, for as far as it goes.
 */
function lineageOf(element: HTMLElement): readonly HTMLElement[] {
  const chain: HTMLElement[] = []
  // The element itself first: where a click does not focus a button (Safari), what
  // has focus when a menu's entry is pressed is the menu's own panel.
  let raisedBy = OPENED_BY.get(element)
  for (let up = element.parentElement; up !== null && up !== document.body; up = up.parentElement) {
    chain.push(up)
    raisedBy ??= OPENED_BY.get(up)
  }
  // Not itself again: an overlay cannot have been opened from inside its own panel.
  if (raisedBy === undefined || raisedBy === element || chain.includes(raisedBy)) return chain
  return [...chain, raisedBy, ...lineageOf(raisedBy)]
}

/**
 * Focuses something that is not focusable, and leaves the tab order as it found it.
 *
 * The same two halves `shell/PortalLayout.tsx` needs for its skip link:
 * `tabindex="-1"` so the element *can* take focus, and an explicit `focus()` because
 * nothing else moves it. The attribute is taken off again on `blur`, so a container
 * that was never in the tab order does not quietly join it — and `base.css` draws no
 * ring on `[tabindex="-1"]`, which is the same silence the route heading and `<main>`
 * are focused in.
 */
function focusSilently(element: HTMLElement): void {
  // A button, a link or a field takes focus as itself (`tabIndex` is 0 for those with
  // no attribute at all): nothing is borrowed, so nothing has to be given back.
  const borrowed = !element.hasAttribute('tabindex') && element.tabIndex < 0
  if (borrowed) {
    element.setAttribute('tabindex', '-1')
    element.addEventListener(
      'blur',
      () => {
        element.removeAttribute('tabindex')
      },
      { once: true },
    )
  }
  element.focus()
}

/**
 * Where focus goes when a dialog closes: the opener, or — when the action it
 * confirmed took the opener with it — the nearest thing still on the page that used
 * to contain it.
 *
 * **The nearest surviving ancestor, and not the top of the page.** A row's *Cancel
 * the invitation* sits in a `<tr>` inside a `<tbody>` inside a card; confirming it
 * removes the `<tr>` and leaves the `<tbody>`, so focus lands exactly where the row
 * was and the next Tab reaches the next row's controls. Sending focus to `<main>`
 * instead would be correct and useless — it is one element away from the `<body>`
 * this is fixing, and a keyboard user would tab the length of the page to get back to
 * the table they were working in.
 *
 * It always finds one, and that is why there is no last resort here: the walk stops
 * below `<body>`, so the worst case is the application's own root element — which is
 * the honest answer for a dialog whose action replaced the entire screen, because for
 * that reader the top of the page genuinely is where they now are.
 *
 * **Nothing at all when nothing had focus to begin with.** A dialog opened by
 * something other than a person leaves the caret where Radix put it, which is the
 * behaviour before this and is still right: there is no place to go back to.
 */
/** Where a dialog was opened from: what had focus, and everything that held it. */
type Origin = { readonly opener: HTMLElement | null; readonly lineage: readonly HTMLElement[] }

/** Opened by nothing a person did: there is no place to go back to. */
const NOWHERE: Origin = { opener: null, lineage: [] }

function originOf(active: Element | null): Origin {
  const from = pressedOver(active) ?? active
  if (!(from instanceof HTMLElement) || from === document.body) return NOWHERE
  return { opener: from, lineage: lineageOf(from) }
}

/**
 * The control a pointer last pressed, until a key is pressed.
 *
 * **A click does not focus what it lands on everywhere.** Safari, and Firefox on a
 * Mac, leave the focus where it was — on `<body>`, or on the nearest thing above the
 * button that takes it — so "what had focus when the window opened" is not the
 * button that opened it, and a window opened with a mouse gave focus back to the top
 * of the page. The press is the one fact every browser agrees on.
 */
const PRESSABLE =
  'button, a[href], select, input, textarea, summary, [role="button"], [role="menuitem"], [role="option"], [role="tab"]'
let pressed: HTMLElement | null = null
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (event) => {
      pressed =
        event.target instanceof Element ? event.target.closest<HTMLElement>(PRESSABLE) : null
    },
    true,
  )
  // A key means the keyboard is what opens the next thing, and focus is where it is.
  document.addEventListener(
    'keydown',
    () => {
      pressed = null
    },
    true,
  )
}

/**
 * The pressed control, where the press did not focus it — or null, and focus is the
 * answer it always was.
 *
 * Null when focus is in the control (the browser focused what was pressed), and null
 * when focus is somewhere that is not above it: a field with the cursor in it, whose
 * calendar refuses focus on purpose, is still where the reader is.
 */
function pressedOver(active: Element | null): HTMLElement | null {
  const control = pressed
  if (control === null || !control.isConnected) return null
  if (active === null || active === document.body) return control
  if (control.contains(active) || !active.contains(control)) return null
  return control
}

function focusAfterClose(opener: HTMLElement | null, lineage: readonly HTMLElement[]): void {
  if (opener === null) return
  if (opener.isConnected) {
    opener.focus()
    // Still there and not taking focus: a submit button is disabled while its write
    // is on its way, which is exactly when the confirmation it raised shuts. Focus
    // was left on `<body>`, under a window that is still open.
    if (document.activeElement === opener) return
  }

  const survivor = lineage.find((one) => one.isConnected)
  if (survivor !== undefined) focusSilently(survivor)
}

export type DialogProps = Common & {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: ReactNode
  /**
   * What the dialog is **about**, under its title — a reference and a name.
   *
   * A window that says "Why is it not being done?" over a form is a window that does
   * not say which task it will cancel, and on a board of thirty rows the answer is
   * not on screen behind it. Every dialog that ends a task carries it.
   */
  readonly about?: ReactNode | undefined
  /**
   * The dismiss control's own words, when "Close" is the wrong one.
   *
   * It is wrong on exactly one kind of window: a task's **Close** is what Mark done
   * does in this product, so a cancel dialog offering "Close" beside "Cancel it"
   * offers two words for finishing a task and neither of them means "go away".
   * "Keep the task" says what walking away leaves behind.
   */
  readonly dismissLabel?: string | undefined
  /** The trigger, when the dialog is opened from one element. */
  readonly trigger?: ReactNode | undefined
  /**
   * Refuses an outside click — and only that: Escape and the dismiss button in the
   * head still close it.
   *
   * Two callers, for one reason each. `ConfirmDialog` sets it always, because losing
   * a money dialog to a stray click is how the wrong thing gets approved on the
   * second attempt. `components/studio/ActionDialog.tsx` sets it **while its form
   * holds unsaved input**, because closing unmounts the form and a click beside a
   * half-written note threw the note away. Nothing else should need to.
   */
  readonly modalOnly?: boolean | undefined
  /**
   * Cannot be shut at all while this is true — not by Escape, not by its own dismiss
   * button, not by a click beside it.
   *
   * One caller: `components/studio/ActionDialog.tsx`, **while its form's own write is
   * on its way**. The form is where the answer is said, and a window shut before the
   * answer took a refusal with it. It is true for the length of one request and the
   * reads that follow, both of which end (`api/hooks.ts` `WRITE_ANSWER_MS`,
   * `READ_AGAIN_MS`): held over anything that has no end it would be a trap, and
   * nothing else should need it.
   */
  readonly held?: boolean | undefined
  /** Wider panel — raise-a-task two-column form (#39). */
  readonly wide?: boolean | undefined
  /**
   * The panel grows with what is typed into it instead of being dragged (session 38).
   *
   * One class rather than a measured height: `field-sizing: content` is the browser
   * doing the measuring, and where it is not supported the box keeps its `rows` and
   * nothing looks broken.
   */
  readonly fit?: boolean | undefined
  readonly children: ReactNode
}

export function Dialog({
  open,
  onOpenChange,
  title,
  about,
  dismissLabel,
  trigger,
  modalOnly,
  held,
  wide,
  fit,
  children,
  ...common
}: DialogProps) {
  const content = useRef<HTMLDivElement | null>(null)

  /*
   * Whatever had focus at the moment this opened, and the chain of what it sat in.
   * `document.body` is not an opener; it is what "nothing had focus" looks like.
   *
   * **Read while the frame that opens it is being rendered**, which is the last
   * moment it is all still there. It was a layout effect — in time for a trigger,
   * which outlives the click — and too late for a window raised by a control that
   * goes away *in the same commit*: Quick create's "More details" replaces one
   * window with another, the button that was pressed is removed before any effect
   * runs, and by then `document.activeElement` is `<body>`. That window closed onto
   * nothing at all.
   *
   * The opener's ancestors are captured now as well and not on the way out, because
   * an element removed from the document has no parents to walk: by the time
   * `onCloseAutoFocus` runs, the row that held the trigger is a detached node and
   * `parentElement` is null all the way up.
   *
   * State, set during render, is how a component follows a prop it was given
   * (`open`) without an effect; the read of the document is the one thing here that
   * is not a function of props, and it is read once per opening.
   */
  const [seenOpen, setSeenOpen] = useState(false)
  const [origin, setOrigin] = useState<Origin>(NOWHERE)
  if (open !== seenOpen) {
    setSeenOpen(open)
    if (open) setOrigin(originOf(document.activeElement))
  }

  /*
   * The panel, and — the moment there is one — what opened it (`OPENED_BY`). A
   * callback rather than the ref object alone because the panel is mounted by Radix's
   * portal a commit after `open` turns true: by then the opener has been read (above,
   * while rendering), and this is the first moment the panel exists to be named.
   */
  const openedBy = origin.opener
  const panelRef = useCallback(
    (panel: HTMLDivElement | null) => {
      content.current = panel
      if (panel !== null && openedBy !== null) OPENED_BY.set(panel, openedBy)
    },
    [openedBy],
  )

  /*
   * An open window holds the page's live reads still (`shell/editing.ts`).
   *
   * A window belongs to the row, the card or the control that opened it, and a
   * refresh that moves that row off the loaded page — or changes the task so the
   * control is no longer offered — unmounts the window with it, and what was typed
   * in it. Here rather than in each caller, so that every dialog there is and every
   * one written next year is covered by saying nothing.
   *
   * `'window'`: what those reads hand the page is held as well, for a read that one
   * of the reader's own writes asks for while this is open — a batch still running,
   * a date saved as this was opened.
   */
  useEditing(open, 'window')

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger === undefined ? null : <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="scrim" />
        <RadixDialog.Content
          ref={panelRef}
          className={cx('dialog', wide === true && 'dialog--wide', fit === true && 'dialog--fit')}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            const panel = content.current
            if (panel === null) return
            // The panel itself is focusable: FocusScope renders it `tabIndex={-1}`,
            // and `base.css` draws no ring on one, so landing here is silent.
            ;(panel.querySelector<HTMLElement>(FIRST_FIELD) ?? panel).focus()
          }}
          onCloseAutoFocus={(event) => {
            // Ours or Radix's, never both: `composeEventHandlers` skips its own
            // handler once this one has prevented the default.
            event.preventDefault()
            focusAfterClose(origin.opener, origin.lineage)
          }}
          {...(modalOnly === true || held === true
            ? {
                onInteractOutside: (event: Event) => {
                  event.preventDefault()
                },
              }
            : {})}
          {...(held === true
            ? {
                onEscapeKeyDown: (event: KeyboardEvent) => {
                  event.preventDefault()
                },
              }
            : {})}
          {...common}
        >
          <div className="dialog__head">
            <RadixDialog.Title className="dialog__title">{title}</RadixDialog.Title>
            {/*
              A plain `Button` rather than `RadixDialog.Close asChild`: `Close` is
              `onOpenChange(false)` with a `Slot` around it, and this component is
              already holding the handle it would call. One element, no clone.
            */}
            <Button
              size="sm"
              variant="ghost"
              disabled={held}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              {dismissLabel ?? DIALOG_CLOSE_LABEL}
            </Button>
            {/*
              A third child of the head rather than a wrapper around the title: the
              head already wraps, so a full-width line drops under both of them —
              and `.dialog__head > .dialog__title` stays the shape the contract test
              reads.
            */}
            {about === undefined ? null : <p className="dialog__about">{about}</p>}
          </div>
          <div className="dialog__body">{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export type ConfirmDialogProps = Common & {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: ReactNode
  /**
   * The whole of the confirm button's label, and it says the amount and the ref:
   * "Approve $1,800.00 for WO-2026-014". Formatted by the page through
   * `useFormat()`, because that is where the `Money` is.
   */
  readonly confirmLabel: string
  readonly onConfirm: () => void
  /** "Later", "Cancel" — whatever the refusal is called on this screen. */
  readonly cancelLabel?: string | undefined
  readonly busy?: boolean | undefined
  /** What is being confirmed, above the buttons. */
  readonly children: ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  confirmLabel,
  onConfirm,
  cancelLabel,
  busy,
  children,
  ...common
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} modalOnly {...common}>
      {children}
      <ButtonRow>
        <Button variant="ochre" onClick={onConfirm} disabled={busy}>
          {confirmLabel}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            onOpenChange(false)
          }}
        >
          {cancelLabel ?? 'Cancel'}
        </Button>
      </ButtonRow>
    </Dialog>
  )
}

export type PopoverProps = Common & {
  readonly trigger: ReactNode
  readonly children: ReactNode
  readonly open?: boolean | undefined
  readonly onOpenChange?: ((open: boolean) => void) | undefined
  /** Overflow menus use `.menu` padding instead of the tip panel’s `.pop`. */
  readonly menu?: boolean | undefined
}

/**
 * A `--fs-2` panel beside its trigger. For "how is this number worked out?".
 *
 * `menu` is not only padding. Radix gives every popover `role="dialog"`, which is
 * right for a panel of prose and wrong for a list of moves: a screen reader was
 * told "dialog" and then read four plain buttons, with nothing saying they were a
 * menu or how many there were. So the content element carries `role="menu"` when it
 * holds one, and the entries inside it are `menuitem`s (`TaskMoreMenu`).
 */
export function Popover({ trigger, children, open, onOpenChange, menu, ...common }: PopoverProps) {
  /*
   * The trigger, so that something raised from inside this panel can find its way
   * back to it once the panel has gone (`OPENED_BY`, `lineageOf`).
   */
  const opener = useRef<HTMLButtonElement | null>(null)
  const panelRef = useCallback((panel: HTMLDivElement | null) => {
    if (panel !== null && opener.current !== null) OPENED_BY.set(panel, opener.current)
  }, [])

  return (
    <RadixPopover.Root
      {...(open === undefined ? {} : { open })}
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
    >
      <RadixPopover.Trigger asChild ref={opener}>
        {trigger}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          ref={panelRef}
          className={menu === true ? 'menu' : 'pop'}
          sideOffset={6}
          {...(menu === true ? { role: 'menu' } : {})}
          {...common}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}

export type SelectMenuOption = { readonly value: string; readonly label: string }

export type SelectMenuProps = Common & {
  readonly label: string
  readonly value: string
  readonly onValueChange: (value: string) => void
  readonly options: readonly SelectMenuOption[]
  readonly disabled?: boolean | undefined
}

/**
 * The studio's list control. `Form.tsx`'s `Select` is the native one and is what
 * a form field should be; this is for a toolbar, where the list is long enough
 * or decorated enough that the platform's own menu is not it.
 *
 * The trigger wears `.select`, so the two look the same on a page that has both.
 */
export function SelectMenu({
  label,
  value,
  onValueChange,
  options,
  disabled,
  ...common
}: SelectMenuProps) {
  return (
    <RadixSelect.Root
      value={value}
      onValueChange={onValueChange}
      {...(disabled === undefined ? {} : { disabled })}
    >
      <RadixSelect.Trigger className="select" aria-label={label} {...common}>
        <RadixSelect.Value />
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content className="menu" position="popper" sideOffset={4}>
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item className="menu__item" key={option.value} value={option.value}>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}
